import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  DocumentDuplicateIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { emailTemplateApi, EmailTemplate } from '../services/emailTemplateApi';
import api from '../services/api';

interface BulkRequestResult {
  leadId: number;
  requestId: number;
  requestToken: string;
  leadName: string;
  leadEmail: string;
  status: 'success' | 'error';
}

interface BulkShareLinksModalProps {
  results: BulkRequestResult[];
  documentTypes: string[];
  onClose: () => void;
  autoOpenEmailModal?: boolean;
}

interface EmailStatus {
  leadId: number;
  status: 'pending' | 'sending' | 'sent' | 'failed';
  error?: string;
}

const BulkShareLinksModal: React.FC<BulkShareLinksModalProps> = ({
  results,
  documentTypes,
  onClose,
  autoOpenEmailModal = false
}) => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(autoOpenEmailModal);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailStatuses, setEmailStatuses] = useState<Map<number, EmailStatus>>(new Map());
  const [emailProgress, setEmailProgress] = useState({ sent: 0, failed: 0, total: 0 });
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [emailSubject, setEmailSubject] = useState(`Document Request - ${documentTypes.join(', ')}`);
  const [emailMessage, setEmailMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Auto-load templates if email modal should open automatically
  useEffect(() => {
    if (autoOpenEmailModal) {
      loadEmailTemplates();
    }
  }, [autoOpenEmailModal]);

  useEffect(() => {
    // Initialize default email message
    const defaultMessage = `Hello {{lead_name}},

We need you to submit the following documents: ${documentTypes.join(', ')}

Please click the link below to upload your documents:
{{document_upload_link}}

If you have any questions, please don't hesitate to contact us.

Thank you for your cooperation.

Best regards,
Your Team`;
    setEmailMessage(defaultMessage);
  }, [documentTypes]);

  // Load email templates
  const loadEmailTemplates = async () => {
    try {
      setLoadingTemplate(true);
      const response = await emailTemplateApi.getDocumentsTemplates();
      
      if (response.success && response.data && response.data.length > 0) {
        setAvailableTemplates(response.data);
        
        // Find the default template or use the first one
        const defaultTemplate = response.data.find(t => t.isDefault) || response.data[0];
        
        if (defaultTemplate) {
          setSelectedTemplate(defaultTemplate);
          setEmailSubject(defaultTemplate.subjectTemplate);
          setEmailMessage(defaultTemplate.bodyTemplate);
        }
      }
    } catch (err) {
      console.error('Error loading email templates:', err);
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleTemplateChange = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setEmailSubject(template.subjectTemplate);
    setEmailMessage(template.bodyTemplate);
  };

  const getShareUrl = (token: string) => {
    return `${window.location.origin}/documents/submit/${token}`;
  };

  const handleCopyLink = (token: string, index: number) => {
    const url = getShareUrl(token);
    navigator.clipboard.writeText(url).then(() => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert('Failed to copy link to clipboard');
    });
  };

  const handleCopyAll = () => {
    const allLinks = results.map(result => {
      const url = getShareUrl(result.requestToken);
      return `${result.leadName} (${result.leadEmail}):\n${url}`;
    }).join('\n\n');

    navigator.clipboard.writeText(allLinks).then(() => {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert('Failed to copy links to clipboard');
    });
  };

  const handleCopyAsCSV = () => {
    const csvHeader = 'Lead Name,Email,Request ID,Share Link\n';
    const csvRows = results.map(result => {
      const url = getShareUrl(result.requestToken);
      return `"${result.leadName}","${result.leadEmail}",${result.requestId},"${url}"`;
    }).join('\n');

    const csv = csvHeader + csvRows;

    navigator.clipboard.writeText(csv).then(() => {
      alert('CSV data copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert('Failed to copy CSV to clipboard');
    });
  };

  const handleDownloadCSV = () => {
    const csvHeader = 'Lead Name,Email,Request ID,Share Link\n';
    const csvRows = results.map(result => {
      const url = getShareUrl(result.requestToken);
      return `"${result.leadName}","${result.leadEmail}",${result.requestId},"${url}"`;
    }).join('\n');

    const csv = csvHeader + csvRows;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `document-requests-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleEmailTemplate = () => {
    const template = results.map(result => {
      const url = getShareUrl(result.requestToken);
      return `To: ${result.leadEmail}
Subject: Document Request - ${documentTypes.join(', ')}

Dear ${result.leadName},

We kindly request you to submit the following documents:
${documentTypes.map(dt => `- ${dt}`).join('\n')}

Please use the link below to upload your documents:
${url}

Thank you for your cooperation.`;
    }).join('\n\n---\n\n');

    navigator.clipboard.writeText(template).then(() => {
      alert('Email templates copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert('Failed to copy email templates');
    });
  };

  const handleSendBulkEmails = async () => {
    if (!window.confirm(`Are you sure you want to send emails to ${results.length} lead(s)?`)) {
      return;
    }

    setSendingEmails(true);
    setError(null);
    setEmailProgress({ sent: 0, failed: 0, total: results.length });

    // Initialize email statuses
    const statuses = new Map<number, EmailStatus>();
    results.forEach(result => {
      statuses.set(result.leadId, { leadId: result.leadId, status: 'pending' });
    });
    setEmailStatuses(new Map(statuses));

    let sentCount = 0;
    let failedCount = 0;

    // Send emails sequentially to avoid overwhelming the email service
    for (const result of results) {
      try {
        // Update status to sending
        statuses.set(result.leadId, { leadId: result.leadId, status: 'sending' });
        setEmailStatuses(new Map(statuses));

        // Fill template with lead-specific data
        const url = getShareUrl(result.requestToken);
        const personalizedSubject = emailSubject
          .replace(/\{\{lead_name\}\}/g, result.leadName)
          .replace(/\{\{document_type\}\}/g, documentTypes.join(', '));
        
        const personalizedMessage = emailMessage
          .replace(/\{\{lead_name\}\}/g, result.leadName)
          .replace(/\{\{lead_email\}\}/g, result.leadEmail)
          .replace(/\{\{document_type\}\}/g, documentTypes.join(', '))
          .replace(/\{\{document_upload_link\}\}/g, url);

        // Send email
        const response = await api.post('/documents/requests/send-email', {
          requestToken: result.requestToken,
          recipientEmail: result.leadEmail,
          recipientName: result.leadName,
          subject: personalizedSubject,
          message: personalizedMessage
        });

        if (response.data.success) {
          sentCount++;
          statuses.set(result.leadId, { leadId: result.leadId, status: 'sent' });
        } else {
          throw new Error(response.data.message || 'Failed to send email');
        }
      } catch (err: any) {
        console.error(`Error sending email to ${result.leadName}:`, err);
        failedCount++;
        statuses.set(result.leadId, { 
          leadId: result.leadId, 
          status: 'failed',
          error: err.response?.data?.message || err.message || 'Failed to send email'
        });
      }

      // Update progress
      setEmailStatuses(new Map(statuses));
      setEmailProgress({ sent: sentCount, failed: failedCount, total: results.length });
    }

    setSendingEmails(false);
    
    // Show summary and close modals
    if (failedCount === 0) {
      setShowEmailModal(false);
      // Small delay to show success message before closing
      setTimeout(() => {
        alert(`✅ Successfully sent ${sentCount} email(s)!`);
        // Close the entire share links modal after success
        onClose();
      }, 100);
    } else if (sentCount > 0) {
      alert(`⚠️ Sent ${sentCount} email(s), but ${failedCount} failed. Check the details below.`);
      // Keep modal open to show error details
    } else {
      setError('All emails failed to send. Please check your email configuration.');
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-[60]">
      <div className="relative top-10 mx-auto p-6 border w-11/12 max-w-4xl shadow-lg rounded-lg bg-white mb-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Document Request Share Links</h3>
            <p className="text-sm text-gray-600 mt-1">
              {results.length} link{results.length !== 1 ? 's' : ''} generated
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Document Types Info */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Requested Documents</h4>
          <div className="flex flex-wrap gap-2">
            {documentTypes.map((docType, index) => (
              <span
                key={index}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
              >
                {docType}
              </span>
            ))}
          </div>
        </div>

        {/* Bulk Actions */}
        <div className="mb-4 flex flex-wrap gap-3">
          <button
            onClick={() => {
              setShowEmailModal(true);
              loadEmailTemplates();
            }}
            disabled={sendingEmails}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <PaperAirplaneIcon className="w-4 h-4 mr-2" />
            {sendingEmails ? 'Sending Emails...' : 'Send Emails to All'}
          </button>
          <button
            onClick={handleCopyAll}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            {copiedAll ? (
              <>
                <CheckIcon className="w-4 h-4 mr-2 text-green-600" />
                Copied!
              </>
            ) : (
              <>
                <ClipboardDocumentIcon className="w-4 h-4 mr-2" />
                Copy All Links
              </>
            )}
          </button>
          <button
            onClick={handleCopyAsCSV}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <DocumentDuplicateIcon className="w-4 h-4 mr-2" />
            Copy as CSV
          </button>
          <button
            onClick={handleDownloadCSV}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download CSV
          </button>
          <button
            onClick={handleEmailTemplate}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <EnvelopeIcon className="w-4 h-4 mr-2" />
            Copy Email Templates
          </button>
        </div>

        {/* Links List */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <div className="grid grid-cols-12 gap-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
              <div className="col-span-4">Lead</div>
              <div className="col-span-6">Share Link</div>
              <div className="col-span-2 text-right">Action</div>
            </div>
          </div>
          <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
            {results.map((result, index) => (
              <div key={result.requestId} className="px-4 py-3 hover:bg-gray-50">
                <div className="grid grid-cols-12 gap-4 items-center">
                  {/* Lead Info */}
                  <div className="col-span-4">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {result.leadName}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {result.leadEmail}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      Request #{result.requestId}
                    </div>
                  </div>

                  {/* Share Link */}
                  <div className="col-span-6">
                    <div className="bg-gray-50 border border-gray-200 rounded px-3 py-2">
                      <input
                        type="text"
                        value={getShareUrl(result.requestToken)}
                        readOnly
                        className="w-full text-xs text-gray-600 bg-transparent border-0 p-0 focus:outline-none focus:ring-0"
                        onClick={(e) => (e.target as HTMLInputElement).select()}
                      />
                    </div>
                  </div>

                  {/* Copy Button */}
                  <div className="col-span-2 text-right">
                    <button
                      onClick={() => handleCopyLink(result.requestToken, index)}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                    >
                      {copiedIndex === index ? (
                        <>
                          <CheckIcon className="w-3 h-3 mr-1 text-green-600" />
                          Copied
                        </>
                      ) : (
                        <>
                          <ClipboardDocumentIcon className="w-3 h-3 mr-1" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer Info */}
        <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">💡 Quick Tips</h4>
          <ul className="space-y-1 text-sm text-gray-600">
            <li>• Use "Copy All Links" to get all links in a formatted list</li>
            <li>• Use "Download CSV" to save links with lead details for record keeping</li>
            <li>• Use "Copy Email Templates" to get pre-formatted emails for each lead</li>
            <li>• Click on any link to select it for easy copying</li>
          </ul>
        </div>

        {/* Close Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2.5 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 overflow-y-auto h-full w-full z-[70] flex items-center justify-center">
          <div className="relative p-6 border w-11/12 max-w-3xl shadow-lg rounded-lg bg-white">
            {/* Email Modal Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Send Bulk Email</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Send document request emails to {results.length} lead{results.length !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={() => {
                  if (!sendingEmails || window.confirm('Email sending is in progress. Are you sure you want to close?')) {
                    setShowEmailModal(false);
                    setEmailStatuses(new Map());
                    setError(null);
                  }
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={sendingEmails}
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                <ExclamationCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Template Selection */}
            {availableTemplates.length > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Template
                </label>
                <select
                  value={selectedTemplate?.id || ''}
                  onChange={(e) => {
                    const template = availableTemplates.find(t => t.id === parseInt(e.target.value));
                    if (template) handleTemplateChange(template);
                  }}
                  disabled={sendingEmails || loadingTemplate}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                >
                  {availableTemplates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.title} {template.isDefault ? '(Default)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Email Subject */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Subject
              </label>
              <input
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                disabled={sendingEmails}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                placeholder="Enter email subject..."
              />
            </div>

            {/* Email Message */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Message
              </label>
              <textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                disabled={sendingEmails}
                rows={10}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 font-mono text-sm"
                placeholder="Enter email message..."
              />
              <p className="mt-2 text-xs text-gray-500">
                Available placeholders: <code className="bg-gray-100 px-1 rounded">{'{{lead_name}}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{{lead_email}}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{{document_type}}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{{document_upload_link}}'}</code>
              </p>
            </div>

            {/* Progress Indicator */}
            {sendingEmails && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-900">Sending Emails...</span>
                  <span className="text-sm text-blue-700">
                    {emailProgress.sent + emailProgress.failed} / {emailProgress.total}
                  </span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2 mb-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((emailProgress.sent + emailProgress.failed) / emailProgress.total) * 100}%` }}
                  ></div>
                </div>
                <div className="flex items-center justify-between text-xs text-blue-700">
                  <span>✅ Sent: {emailProgress.sent}</span>
                  <span>❌ Failed: {emailProgress.failed}</span>
                </div>
              </div>
            )}

            {/* Email Statuses */}
            {emailStatuses.size > 0 && (
              <div className="mb-4 max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                <div className="divide-y divide-gray-100">
                  {results.map(result => {
                    const status = emailStatuses.get(result.leadId);
                    if (!status) return null;

                    return (
                      <div key={result.leadId} className="p-3 flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {result.leadName}
                          </div>
                          <div className="text-xs text-gray-500 truncate">{result.leadEmail}</div>
                          {status.error && (
                            <div className="text-xs text-red-600 mt-1">{status.error}</div>
                          )}
                        </div>
                        <div className="ml-3 flex-shrink-0">
                          {status.status === 'pending' && (
                            <span className="text-xs text-gray-500">Pending</span>
                          )}
                          {status.status === 'sending' && (
                            <div className="flex items-center text-blue-600">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                              <span className="text-xs">Sending...</span>
                            </div>
                          )}
                          {status.status === 'sent' && (
                            <span className="inline-flex items-center text-xs text-green-600">
                              <CheckIcon className="w-4 h-4 mr-1" />
                              Sent
                            </span>
                          )}
                          {status.status === 'failed' && (
                            <span className="inline-flex items-center text-xs text-red-600">
                              <ExclamationCircleIcon className="w-4 h-4 mr-1" />
                              Failed
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  if (!sendingEmails || window.confirm('Email sending is in progress. Are you sure you want to cancel?')) {
                    setShowEmailModal(false);
                    setEmailStatuses(new Map());
                    setError(null);
                  }
                }}
                disabled={sendingEmails}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSendBulkEmails}
                disabled={sendingEmails || !emailSubject.trim() || !emailMessage.trim()}
                className="px-5 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {sendingEmails ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </div>
                ) : (
                  `Send to ${results.length} Lead${results.length !== 1 ? 's' : ''}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkShareLinksModal;
